import Image from 'next/image';
import { Dialog, Transition } from '@headlessui/react';
import { useRef, Fragment, useCallback } from 'react';
import SwiperCore, { Autoplay, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AiFillCaretLeft, AiFillCaretRight } from 'react-icons/ai';
import { IoMdClose } from 'react-icons/io';

import { MediaQuery, useMediaQuery } from '../../../hooks/useMediaQuery';
import {
  oneAndOneFifthSlidesPerView,
  oneSlidePerView
} from '../../../constants/modals';
import { getSlidesPerView } from '../../../utils/carousel';
import 'swiper/css';
import 'swiper/css/navigation';

/* eslint-disable-next-line */
type CardModalProps = {
  imageHeight?: number;
  isOpen: boolean;
  singleImageHeight?: number;
  src: string | Array<string>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export function CardModal(props: CardModalProps) {
  const {
    imageHeight = 55,
    isOpen,
    singleImageHeight = 55,
    src,
    setIsOpen
  } = props;

  const isDesktop = useMediaQuery(MediaQuery.LG);
  const slidesPerView = getSlidesPerView(
    oneAndOneFifthSlidesPerView,
    oneSlidePerView,
    isDesktop
  );

  const sliderRef = useRef() as any;

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  function handleClose() {
    setIsOpen(false);
  }

  const modalImage = (
    <Image
      alt="modal-image"
      height={singleImageHeight}
      layout="responsive"
      objectFit="contain"
      src={src as string}
      width="100%"
    />
  );

  const slides =
    Array.isArray(src) &&
    src?.map((src) => {
      return (
        <SwiperSlide key={src}>
          <Image
            alt="modal-image"
            blurDataURL="/icons/blur-placeholder.png"
            height={imageHeight}
            layout="responsive"
            objectFit="contain"
            placeholder="blur"
            src={src as string}
            width="100%"
          />
        </SwiperSlide>
      );
    });

  const carousel = Array.isArray(src) && (
    <Swiper
      centeredSlides={true}
      className="imageCarousel"
      loop={true}
      modules={[Autoplay, Navigation]}
      onInit={(core: SwiperCore) => {
        sliderRef.current = core.el;
      }}
      slidesPerView={slidesPerView}
      spaceBetween={20}
      speed={900}
    >
      {slides}
    </Swiper>
  );

  const navigation = Array.isArray(src) && !isDesktop && (
    <>
      <AiFillCaretLeft
        className="absolute top-2/4 -translate-y-2/4 text-header3 text-headerBackground z-50 cursor-pointer"
        onClick={handlePrev}
      />
      <AiFillCaretRight
        className="absolute right-0 top-2/4 -translate-y-2/4 text-header3 text-headerBackground z-50 cursor-pointer"
        onClick={handleNext}
      />
    </>
  );

  const modalContent = Array.isArray(src) ? carousel : modalImage;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-visible"
        onClose={setIsOpen}
      >
        <div className="h-full w-full block relative">
          {navigation}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="w-full lg:w-3/5 lg:px-5 absolute !top-2/4 left-2/4 -translate-x-2/4 !-translate-y-2/4">
              {modalContent}
              <IoMdClose
                className="absolute -top-6 right-4 text-white text-header5 cursor-pointer"
                onClick={handleClose}
              />
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default CardModal;
